/**
 * Date utilities for handling timezone-safe date operations
 * Specifically designed to prevent August→September month offset issues
 */

/**
 * Formats a Date object for backend storage without timezone conversion
 * Returns format: YYYY-MM-DD HH:MM:SS
 */
export const formatDateForBackend = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

/**
 * Formats a Date object for datetime-local input
 * Returns format: YYYY-MM-DDTHH:MM
 */
export const formatDateForInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Parses a datetime-local input string to a Date object in local timezone
 * Input format: YYYY-MM-DDTHH:MM
 */
export const parseDateFromInput = (dateTimeLocalString: string): Date => {
  const [datePart, timePart] = dateTimeLocalString.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);
  
  // Create date in local timezone (month is 0-based)
  const localDate = new Date(year, month - 1, day, hours, minutes, 0, 0);
  
  console.log('📅 Date parsing:', {
    input: dateTimeLocalString,
    parsed: { year, month: month - 1, day, hours, minutes },
    resultDate: localDate,
    resultString: localDate.toLocaleDateString('it-IT') + ' ' + localDate.toLocaleTimeString('it-IT')
  });
  
  return localDate;
};

/**
 * Creates a date for a specific day with a specific time, preserving the local timezone
 * Useful for calendar day assignments
 */
export const createDateWithTime = (date: Date, hours: number = 9, minutes: number = 0): Date => {
  console.log('🔍 createDateWithTime input:', {
    inputDate: date,
    inputDateString: date.toLocaleDateString('it-IT'),
    inputMonth: date.getMonth() + 1,
    inputMonthName: date.toLocaleString('it', { month: 'long' }),
    inputDay: date.getDate(),
    inputYear: date.getFullYear(),
    requestedHours: hours,
    requestedMinutes: minutes
  });
  
  const result = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    hours,
    minutes,
    0,
    0
  );
  
  console.log('🔍 createDateWithTime output:', {
    resultDate: result,
    resultDateString: result.toLocaleDateString('it-IT'),
    resultMonth: result.getMonth() + 1,
    resultMonthName: result.toLocaleString('it', { month: 'long' }),
    resultDay: result.getDate(),
    resultYear: result.getFullYear(),
    monthShift: (date.getMonth() + 1) !== (result.getMonth() + 1)
  });
  
  return result;
};

/**
 * Validates that a date has the correct month to prevent August→September issues
 */
export const validateDateMonth = (originalDate: Date, processedDate: Date): boolean => {
  const originalMonth = originalDate.getMonth();
  const processedMonth = processedDate.getMonth();
  
  if (originalMonth !== processedMonth) {
    console.error('🚨 Month mismatch detected!', {
      originalMonth,
      processedMonth,
      originalDate: originalDate.toLocaleDateString('it-IT'),
      processedDate: processedDate.toLocaleDateString('it-IT')
    });
    return false;
  }
  
  return true;
};

/**
 * Safe date serialization that preserves month information
 */
export const safeSerializeDate = (date: Date): string => {
  const serialized = formatDateForBackend(date);
  
  // Validate that serialization didn't change the month
  const testDate = new Date(serialized);
  if (!validateDateMonth(date, testDate)) {
    console.warn('⚠️ Date serialization caused month shift, using fallback method');
    // Fallback to a more explicit format
    return formatDateForBackend(date);
  }
  
  return serialized;
};